/**
 * UI store for Clone Table feature.
 * Manages dialog state and clone operation tracking.
 */

import { create } from 'zustand';
import type { CloneTableProgressEvent } from '../types/cloneTable';

type CloneOperationStatus =
  | 'idle'
  | 'in-progress'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

type CloneTableOperation = {
  operationId: string;
  status: CloneOperationStatus;
  sourceTableName: string;
  targetTableName: string;
  progress?: number;
  message?: string;
  error?: string;
  rowsCopied?: number;
  totalRows?: number;
  /** Target table left on the server by a failed clone (see CloneTableProgressEvent). */
  partialTargetTable?: string;
  startTime: number;
};

type CloneTableUiState = {
  // Active operations
  operations: CloneTableOperation[];

  // Actions
  addOperation: (
    operationId: string,
    sourceTableName: string,
    targetTableName: string
  ) => void;
  updateOperation: (
    operationId: string,
    updates: Partial<CloneTableOperation>
  ) => void;
  updateOperationFromProgress: (event: CloneTableProgressEvent) => void;
  removeOperation: (operationId: string) => void;
  getOperation: (operationId: string) => CloneTableOperation | undefined;
};

export const useCloneTableUiStore = create<CloneTableUiState>((set, get) => ({
  operations: [],

  addOperation: (operationId, sourceTableName, targetTableName) => {
    set((state) => ({
      operations: [
        ...state.operations,
        {
          operationId,
          status: 'in-progress',
          sourceTableName,
          targetTableName,
          startTime: Date.now(),
        },
      ],
    }));
  },

  updateOperation: (operationId, updates) => {
    set((state) => ({
      operations: state.operations.map((op) =>
        op.operationId === operationId ? { ...op, ...updates } : op
      ),
    }));
  },

  updateOperationFromProgress: (event) => {
    const statusMap: Record<string, CloneOperationStatus> = {
      preflight: 'in-progress',
      'creating-table': 'in-progress',
      'creating-constraints': 'in-progress',
      'creating-indexes': 'in-progress',
      'copying-data': 'in-progress',
      complete: 'succeeded',
      failed: 'failed',
      cancelled: 'cancelled',
    };

    set((state) => {
      const existing = state.operations.find((op) => op.operationId === event.operationId);
      if (existing) {
        return {
          operations: state.operations.map((op) =>
            op.operationId === event.operationId
              ? {
                  ...op,
                  status: statusMap[event.phase] || op.status,
                  progress: event.progress ?? op.progress,
                  message: event.message ?? op.message,
                  error: event.error ?? op.error,
                  rowsCopied: event.rowsCopied ?? op.rowsCopied,
                  totalRows: event.totalRows ?? op.totalRows,
                  partialTargetTable: event.partialTargetTable ?? op.partialTargetTable,
                }
              : op
          ),
        };
      }
      // Upsert: events can arrive before the operation is registered (race between
      // main emitting events synchronously vs renderer subscribing after React render).
      return {
        operations: [
          ...state.operations,
          {
            operationId: event.operationId,
            status: statusMap[event.phase] || 'in-progress',
            sourceTableName: '',
            targetTableName: '',
            startTime: Date.now(),
            progress: event.progress,
            message: event.message,
            error: event.error,
            rowsCopied: event.rowsCopied,
            totalRows: event.totalRows,
            partialTargetTable: event.partialTargetTable,
          },
        ],
      };
    });
  },

  removeOperation: (operationId) => {
    set((state) => ({
      operations: state.operations.filter(
        (op) => op.operationId !== operationId
      ),
    }));
  },

  getOperation: (operationId) => {
    return get().operations.find((op) => op.operationId === operationId);
  },
}));
